/**
 * DistrictAdminScreen.tsx — district IT admin / FERPA officer view.
 *
 * Two responsibilities:
 *   1. Mint invite codes for new SLPs joining the district.
 *   2. Show the district's members + their roles.
 *   3. Show the district's audit log (FERPA "accounting of
 *      disclosures" — the procurement officer's view).
 *
 * Why this lives in the web tier, not iOS:
 *   - Districts use laptops/web consoles for admin tasks, not
 *     iPads. The iOS app is SLP-facing; admins are different
 *     user personas with different UX needs.
 *   - The web tier already has multi-row tables, CSV export
 *     paths, and copy-to-clipboard affordances. iOS would be
 *     reinventing those.
 *
 * Source-of-truth: docs/security/FERPA_POSTURE.md §6.
 */

import { useEffect, useState, useCallback } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase/client';

type AdminScreenProps = {
  onBack: () => void;
};

type MembershipRow = {
  user_id: string;
  role: 'member' | 'admin' | 'billing_admin';
  joined_at: string;
  email: string | null;
};

type InviteRow = {
  id: string;
  code: string;
  role: 'member' | 'admin' | 'billing_admin';
  email: string | null;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  used_by: string | null;
};

type AuditRow = {
  accessed_at: string;
  action: 'read' | 'read_batched' | 'write' | 'delete';
  resource_kind: string;
  resource_id: string | null;
  target_organization_id: string | null;
  accessed_by: string;
};

type OrgInfo = {
  id: string;
  name: string;
  state: string | null;
};

function generateInviteCode(): string {
  // 8-char A-Z0-9 excluding I/O/0/1 for legibility. Random
  // enough for invite-rate limiting (collision probability
  // ~ 1/2^35 for 1M active invites).
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
  }
  return code;
}

async function loadOrgForCurrentUser(
  client: SupabaseClient,
): Promise<OrgInfo | null> {
  // An admin might have multiple memberships; pick the first
  // org where their role is 'admin' or 'billing_admin'.
  const { data, error } = await client
    .from('organization_memberships')
    .select('role, organizations:organization_id(id, name, state)')
    .in('role', ['admin', 'billing_admin'])
    .limit(1)
    .single();
  if (error || !data) return null;
  // The Supabase typed response nests the related row under
  // the foreign-key name. Pull it out explicitly.
  const org = (data as unknown as { organizations: OrgInfo })
    .organizations;
  if (!org?.id) return null;
  return org;
}

export function DistrictAdminScreen({
  onBack,
}: AdminScreenProps): React.ReactElement {
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [memberships, setMemberships] = useState<MembershipRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [minting, setMinting] = useState<boolean>(false);
  const [mintEmail, setMintEmail] = useState<string>('');

  const reload = useCallback(async () => {
    const adminOrg = await loadOrgForCurrentUser(supabase);
    if (!adminOrg) {
      setError(
        'You are not an admin of any school district. ' +
          'Sign in as the user who minted your account, or have your district admin invite you with the "admin" role.',
      );
      return;
    }
    setOrg(adminOrg);

    const [membersResp, invitesResp, auditResp] = await Promise.all([
      supabase
        .from('organization_memberships')
        .select('user_id, role, joined_at, users:user_id(email)')
        .eq('organization_id', adminOrg.id)
        .order('joined_at', { ascending: false }),
      supabase
        .from('organization_invites')
        .select('id, code, role, email, created_at, expires_at, used_at, used_by')
        .eq('organization_id', adminOrg.id)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('tenant_access_log')
        .select('accessed_at, action, resource_kind, resource_id, target_organization_id, accessed_by')
        .eq('target_organization_id', adminOrg.id)
        .order('accessed_at', { ascending: false })
        .limit(200),
    ]);
    if (membersResp.error) {
      setError(`Couldn't load members: ${membersResp.error.message}`);
      return;
    }
    if (invitesResp.error) {
      setError(`Couldn't load invites: ${invitesResp.error.message}`);
      return;
    }
    if (auditResp.error) {
      setError(`Couldn't load audit log: ${auditResp.error.message}`);
      return;
    }
    // Flatten the email join. The Supabase client returns the
    // nested `users` row as an array even for to-one relations;
    // pull the first email.
    setMemberships(
      (membersResp.data ?? []).map((m) => {
        const joined = m as MembershipRow & {
          users: { email: string | null }[] | { email: string | null } | null;
        };
        let email: string | null = null;
        const u = joined.users;
        if (Array.isArray(u) && u.length > 0) {
          email = u[0]?.email ?? null;
        } else if (u && !Array.isArray(u)) {
          email = u.email ?? null;
        }
        return {
          user_id: joined.user_id,
          role: joined.role,
          joined_at: joined.joined_at,
          email,
        };
      }),
    );
    setInvites(invitesResp.data ?? []);
    setAudit(auditResp.data ?? []);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function mintInvite(): Promise<void> {
    if (!org) return;
    setMinting(true);
    setError(null);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (!userId) throw new Error('Not signed in');
      const code = generateInviteCode();
      const expiresAt = new Date(
        Date.now() + 30 * 24 * 60 * 60 * 1000,
      ).toISOString();
      const { error: insertErr } = await supabase
        .from('organization_invites')
        .insert({
          organization_id: org.id,
          code,
          role: 'member',
          email: mintEmail.trim() || null,
          created_by: userId,
          expires_at: expiresAt,
        });
      if (insertErr) throw insertErr;
      setMintEmail('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setMinting(false);
  }

  async function copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older browsers / non-secure contexts. Fall back silently;
      // the user can still select-and-copy from the visible code.
    }
  }

  if (error && !org) {
    return (
      <main className="screen admin">
        <header className="screen-header">
          <button type="button" className="link-button" onClick={onBack}>← Back</button>
          <h1>District admin</h1>
        </header>
        <p className="error">{error}</p>
      </main>
    );
  }

  return (
    <main className="screen admin">
      <header className="screen-header">
        <button type="button" className="link-button" onClick={onBack}>← Back</button>
        <h1>{org ? org.name : 'District admin'}</h1>
        {org?.state && <p className="tagline">State: {org.state}</p>}
      </header>

      {error && <p className="error">{error}</p>}

      <section className="admin-section">
        <h2>Invite a new clinician</h2>
        <p className="tagline">
          Share the 8-character code (or the email pre-bake, if
          you typed one) with the SLP. They paste it in Settings →
          Join a district on the iOS app. Codes expire after 30 days.
        </p>
        <div className="form-row">
          <input
            type="email"
            placeholder="(optional) pre-bake email"
            value={mintEmail}
            onChange={(e) => setMintEmail(e.target.value)}
          />
          <button
            type="button"
            className="snap-button"
            onClick={mintInvite}
            disabled={minting}
          >
            {minting ? 'Minting…' : 'Mint invite'}
          </button>
        </div>
      </section>

      <section className="admin-section">
        <h2>Active invites ({invites.length})</h2>
        {invites.length === 0
          ? <p className="tagline">No invites yet.</p>
          : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Role</th>
                  <th>Email pre-bake</th>
                  <th>Expires</th>
                  <th>Used</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <button
                        type="button"
                        className="code-link"
                        onClick={() => void copyToClipboard(inv.code)}
                        title="Click to copy"
                      >
                        {inv.code}
                      </button>
                    </td>
                    <td><span className="chip">{inv.role}</span></td>
                    <td>{inv.email ?? '—'}</td>
                    <td>{new Date(inv.expires_at).toLocaleDateString()}</td>
                    <td>
                      {inv.used_at
                        ? new Date(inv.used_at).toLocaleDateString()
                        : <span className="tagline">unused</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </section>

      <section className="admin-section">
        <h2>Members ({memberships.length})</h2>
        {memberships.length === 0
          ? <p className="tagline">No members yet.</p>
          : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {memberships.map((m) => (
                  <tr key={m.user_id}>
                    <td>{m.email ?? m.user_id}</td>
                    <td><span className="chip">{m.role}</span></td>
                    <td>{new Date(m.joined_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </section>

      <section className="admin-section">
        <h2>Audit log — last 200 events targeting this district</h2>
        <p className="tagline">
          FERPA "accounting of disclosures". Every cross-tenant
          read/write/delete targeting your district is logged here.
          Same-tenant reads are batched hourly into a count summary.
        </p>
        {audit.length === 0
          ? <p className="tagline">No cross-tenant access recorded yet.</p>
          : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Resource ID</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((row, idx) => (
                  <tr key={`${row.accessed_at}-${idx}`}>
                    <td>{new Date(row.accessed_at).toLocaleString()}</td>
                    <td>{row.action}</td>
                    <td>{row.resource_kind}</td>
                    <td>
                      <code>{row.resource_id?.slice(0, 8) ?? '—'}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </section>
    </main>
  );
}